import { useId, useRef, useState } from 'react';
import { Dialog } from '../ui/Dialog/Dialog';
import { Text } from '../atoms/Typography/Text';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { Inline } from '../atoms/Inline/Inline';
import { useTranslation } from '../../i18n/useTranslation';
import './CameraSourceForm.css';
import { Alert } from '../atoms/Alert/Alert';

/** 24-hour HH:mm, matching the API's expected_capture_times validation. */
const TIME_FORMAT_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

export interface CameraSourceFormValues {
  name: string;
  expectedCaptureTimes: string[];
}

export interface CameraSourceFormProps {
  /** Whether the dialog is open. */
  isOpen: boolean;
  /** Initial values; when provided, the form behaves as an "edit" form. */
  initialValues?: CameraSourceFormValues;
  /** Called with the validated form values when the user submits successfully. */
  onSubmit: (values: CameraSourceFormValues) => void;
  /** Called when the user cancels or closes the dialog (Cancel, Escape, close button). */
  onCancel: () => void;
  /** Shows the spec's submit error ("Couldn't save this camera source…") after a failed save upstream. */
  submitError?: boolean;
  /** Whether a submission is currently in progress (disables the actions). */
  isSubmitting?: boolean;
}

interface TimeRow {
  key: number;
  value: string;
}

interface FormErrors {
  name?: boolean;
  times: Record<number, boolean>;
}

/**
 * Create/edit dialog for a camera source (design spec #1436, "Camera Source
 * Form" fragment). Presentational: validates name (required) and capture
 * times (HH:mm) client-side and hands the values to `onSubmit`.
 */
export function CameraSourceForm({
  isOpen,
  initialValues,
  onSubmit,
  onCancel,
  submitError = false,
  isSubmitting = false,
}: CameraSourceFormProps) {
  const { t } = useTranslation();
  const idPrefix = useId();
  const nextKey = useRef(0);
  const makeRow = (value: string): TimeRow => ({ key: nextKey.current++, value });

  const [name, setName] = useState(initialValues?.name ?? '');
  const [times, setTimes] = useState<TimeRow[]>(() =>
    (initialValues?.expectedCaptureTimes ?? []).map(makeRow),
  );
  const [errors, setErrors] = useState<FormErrors>({ times: {} });

  const handleAddTime = () => {
    setTimes(prev => [...prev, makeRow('')]);
  };

  const handleRemoveTime = (key: number) => {
    setTimes(prev => prev.filter(row => row.key !== key));
    setErrors(prev => {
      const { [key]: _removed, ...rest } = prev.times;
      return { ...prev, times: rest };
    });
  };

  const handleTimeChange = (key: number, value: string) => {
    setTimes(prev => prev.map(row => (row.key === key ? { ...row, value } : row)));
  };

  const handleSave = () => {
    const nextErrors: FormErrors = { times: {} };
    if (name.trim().length === 0) nextErrors.name = true;
    times.forEach(row => {
      if (!TIME_FORMAT_REGEX.test(row.value.trim())) nextErrors.times[row.key] = true;
    });
    setErrors(nextErrors);

    if (nextErrors.name || Object.keys(nextErrors.times).length > 0) return;

    onSubmit({
      name: name.trim(),
      expectedCaptureTimes: times.map(row => row.value.trim()),
    });
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      title={initialValues ? t('cameraSources.form.editTitle') : t('cameraSources.form.createTitle')}
    >
      <Stack gap="4">
        <Input
          id={`${idPrefix}-name`}
          label={t('cameraSources.form.name.label')}
          placeholder={t('cameraSources.form.name.placeholder')}
          value={name}
          onChange={e => setName(e.target.value)}
          error={errors.name ? t('cameraSources.form.name.error') : undefined}
          required
          fullWidth
        />

        <Stack gap="2">
          <Text variant="label">{t('cameraSources.form.times.label')}</Text>
          <Stack gap="2">
            {times.map(row => (
              <Inline key={row.key} gap="2" align="end" className="camera-source-form__time-row">
                <Input
                  id={`${idPrefix}-time-${row.key}`}
                  label={t('cameraSources.form.time.label')}
                  type="time"
                  value={row.value}
                  onChange={e => handleTimeChange(row.key, e.target.value)}
                  error={errors.times[row.key] ? t('cameraSources.form.time.error') : undefined}
                />
                <Button variant="ghost" onClick={() => handleRemoveTime(row.key)}>
                  {t('cameraSources.form.time.remove')}
                </Button>
              </Inline>
            ))}
            <Inline>
              <Button variant="secondary" onClick={handleAddTime}>
                {t('cameraSources.form.time.add')}
              </Button>
            </Inline>
          </Stack>
        </Stack>

        {submitError && (
          <Alert>{t('cameraSources.form.submitError')}</Alert>
        )}

        <Inline justify="end" gap="2">
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            {t('cameraSources.form.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
            {t('cameraSources.form.save')}
          </Button>
        </Inline>
      </Stack>
    </Dialog>
  );
}

export default CameraSourceForm;
